<script setup lang="ts">
import { CirclePlus, CopyDocument, Fold } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  FormDefinition,
  ShortcodeAction,
  ShortcodeDetail,
  Workflow,
  WorkflowDefinition,
} from "~/api";
import RefreshButton from "~/components/RefreshButton.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";
import { getEnvConfig } from "~/services/appConfig";
import ShortcodeEditor from "../components/ShortcodeEditor.vue";

const loading = ref(true);

const props = defineProps<{
  workflowDefinition: WorkflowDefinition;
}>();

const shortcodeDetailList = ref<ShortcodeDetail[]>([]);

// Computed properties to filter shortcodes by action type
const createWorkflowShortcodes = computed(() => {
  return shortcodeDetailList.value.filter(
    (sc) => sc.action === ShortcodeAction.CREATE_WORKFLOW_FOR_USER,
  );
});

const createFormResponseTemplateShortcodes = computed(() => {
  return shortcodeDetailList.value.filter(
    (sc) =>
      sc.action === ShortcodeAction.CREATE_FORM_RESPONSE_IN_WORKFLOW &&
      !sc.targetWorkflowGuid,
  );
});

const createFormResponseInWorkflowShortcodes = computed(() => {
  return shortcodeDetailList.value.filter(
    (sc) =>
      sc.action === ShortcodeAction.CREATE_FORM_RESPONSE_IN_WORKFLOW &&
      sc.targetWorkflowGuid,
  );
});

const deleteShortcode = async (shortcode: ShortcodeDetail) => {
  if (!shortcode || !shortcode.code) {
    return;
  }
  const code = shortcode.code;

  ElMessageBox.confirm(
    `This will delete the '${shortcode.description}' link. If being used externally, the link will no longer work.`,
    "Are you sure?",
    {
      cancelButtonText: "Yes, Delete",
      confirmButtonText: "No",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "error",
    },
  )
    .then(() => {
      // do nothing
    })
    .catch(() => {
      apiClient.shortcode
        .deleteShortcode(myInfoStore.currentSysOrgGuid, code)
        .then((res) => {
          // remove this from our list
          ElMessage.success(res);
          console.log(`Shortcode '${shortcode.description}' deleted`);
          shortcodeDetailList.value = shortcodeDetailList.value.filter(
            (sc) => sc.code !== code,
          );
        })
        .catch(showError);
    });
};

const getShortcodeFullUrl = (code: string) => {
  return `${getEnvConfig().entraRedirect}/start/${code}`;
};

const copyUrl = (row: ShortcodeDetail) => {
  if (!row.code) {
    showError("Shortcode code is missing");
    return;
  }
  const fullUrl = getShortcodeFullUrl(row.code);
  navigator.clipboard.writeText(fullUrl).then(() => {
    ElMessage.success("Shortcode URL copied to clipboard");
  });
};

const selectedRow = ref<ShortcodeDetail | undefined>({} as ShortcodeDetail);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();

const showList = (res: ShortcodeDetail[]) => {
  shortcodeDetailList.value = res;
  sortList();
  loading.value = false;
};

const referencedWorkflowInstances = ref<Workflow[]>([]);

const workflowDetails = (guid: string | null | undefined) => {
  if (!guid) {
    return "Unknown Workflow";
  }
  const workflow = referencedWorkflowInstances.value.find(
    (wf) => wf.guid === guid,
  );
  return workflow ? workflow.name : "Unknown Workflow";
};

const refreshList = async () => {
  loading.value = true;
  const list = apiClient.shortcode
    .getShortcodesAsync(
      myInfoStore.currentSysOrgGuid,
      props.workflowDefinition.guid,
    )
    .then((res) => {
      showList(res.list!);
      referencedWorkflowInstances.value = res.workflows ?? [];
    });

  if (createFormResponseInWorkflowShortcodes.value.length > 0) {
    // get the names of the workflow instances mentioned
  }

  return list;
};

onMounted(async () => {
  refreshList();
});

const sortList = () => {
  shortcodeDetailList.value.sort((a: ShortcodeDetail, b: ShortcodeDetail) => {
    return (a.description ?? "").localeCompare(b.description ?? "");
  });
};

const updated = (item: ShortcodeDetail) => {
  const index = shortcodeDetailList.value.findIndex(
    (f) => f.code === item.code,
  );
  if (index !== -1) {
    shortcodeDetailList.value[index] = item;
  } else {
    shortcodeDetailList.value.push(item);
  }
  sortList();
  drawerOpen.value = false;
};

// Load a specific workflow definition to get its forms
const multipleInstanceFormDefinitions = computed(() => {
  return (
    props.workflowDefinition.formDefinitions?.filter(
      (form: FormDefinition) =>
        form.metaInfo && form.metaInfo.multipleInstancesAllowed === true,
    ) ?? []
  );
});

const shortcodeBlank = {
  id: -1, // temporary id for new items
  code: "", // needs to be here but will be generated on the server
  sysOrgGuid: myInfoStore.currentSysOrgGuid,
  targetWorkflowDefinitionGuid: props.workflowDefinition.guid,
  maxUses: 0, // unlimited
} as ShortcodeDetail;

// const lastSavedJson = ref<string>("");

// const updateLastUpdated = () => {
//   lastSavedJson.value = JSON.stringify(emailTemplate.value);
// };

const createShortcode = (action: ShortcodeAction) => {
  selectedRow.value = { ...shortcodeBlank };
  selectedRow.value.action = action;
  drawerOpen.value = true;
};

/// Show the clicked item in the drawer
const showItem = (row: ShortcodeDetail) => {
  selectedRow.value = { ...row };
  drawerOpen.value = true;
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};
</script>

<template>
  <div class="shortcodeListContainer">
    <div class="top-right">
      <RefreshButton @click="refreshList" />
    </div>

    <p>Shortcodes are used by appending them to the site URL.</p>
    <p>
      For example:
      <code>{{
        getShortcodeFullUrl(
          createWorkflowShortcodes[0]?.code ??
            createFormResponseTemplateShortcodes[0]?.code ??
            "[shortcode]",
        )
      }}</code>
    </p>

    <!-- Create Workflow Shortcodes Section -->
    <div class="shortcode-section">
      <h2>Create a new Workflow</h2>
      <p class="section-description">
        Optional. If created, can be used by anyone to create a new workflow for
        themselves.
      </p>
      <el-button
        v-if="createWorkflowShortcodes.length === 0"
        :icon="CirclePlus"
        title="Add New Shortcode"
        class="addButton"
        @click="createShortcode(ShortcodeAction.CREATE_WORKFLOW_FOR_USER)"
      >
        New Shortcode
      </el-button>
      <el-table v-loading="loading" :data="createWorkflowShortcodes">
        <el-table-column label="Description" width="400">
          <template #default="{ row }: Slotted<ShortcodeDetail>">
            <el-link :icon="Fold" underline="never" @click="showItem(row)">
              {{ row.description }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="Code" width="200">
          <template #default="{ row }: Slotted<ShortcodeDetail>">
            <span class="code">{{ row.code }}</span>
            <el-link
              :icon="CopyDocument"
              underline="never"
              title="Copy full shortcode URL"
              @click="copyUrl(row)"
            >
            </el-link>
          </template>
        </el-table-column>
        <el-table-column label="Uses" width="200">
          <template #default="{ row }: Slotted<ShortcodeDetail>">
            {{ row.useCount || 0 }} / {{ row.maxUses || "unlimited" }}
          </template>
        </el-table-column>
        <el-table-column width="100">
          <template #default="{ row }: Slotted<ShortcodeDetail>">
            <el-button size="small" @click="deleteShortcode(row)">
              Delete
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Create Form Response Shortcodes Section -->
    <div class="shortcode-section">
      <h2>Create a template for a new Form Response in a Workflow</h2>
      <p class="section-description">
        This creates a template that will be generated when a workflow of this
        type is created.
      </p>
      <div v-if="multipleInstanceFormDefinitions.length === 0">
        Cannot create a FormResponse shortcode because there are no forms with
        multiple instances allowed found in this workflow.
      </div>
      <div v-else>
        <el-button
          :icon="CirclePlus"
          title="Add New Shortcode"
          class="addButton"
          @click="
            createShortcode(ShortcodeAction.CREATE_FORM_RESPONSE_IN_WORKFLOW)
          "
        >
          New Shortcode
        </el-button>
        <el-table
          v-loading="loading"
          :data="createFormResponseTemplateShortcodes"
        >
          <el-table-column label="Description" width="400">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              <el-link :icon="Fold" underline="never" @click="showItem(row)">
                {{ row.description }}
              </el-link>
            </template>
          </el-table-column>
          <el-table-column prop="id" label="ID" width="75" align="center">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              <span class="code">{{ row.id }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Uses" width="200">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              {{ row.useCount || 0 }} / {{ row.maxUses || "unlimited" }}
            </template>
          </el-table-column>
          <el-table-column width="100">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              <el-button size="small" @click="deleteShortcode(row)">
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- Create Form Response Shortcodes Section -->
    <div class="shortcode-section">
      <h2>Shortcodes for existing workflow instances</h2>
      <p class="section-description">
        Total: {{ createFormResponseInWorkflowShortcodes.length }}. These have
        been generated when a new Workflow is created for a user. The shortcode
        link for each workflow instance can be shared to others.
      </p>
      <div>
        <el-table
          v-loading="loading"
          :data="createFormResponseInWorkflowShortcodes"
          :max-height="500"
        >
          <el-table-column label="Workflow Instance Name" width="300">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              {{ workflowDetails(row.targetWorkflowGuid) }}
            </template>
          </el-table-column>
          <el-table-column label="Description" width="400">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              {{ row.description }}
            </template>
          </el-table-column>

          <el-table-column prop="name" label="Code" width="200">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              <span class="code">{{ row.code }}</span>
              <el-link
                :icon="CopyDocument"
                underline="never"
                title="Copy full shortcode URL"
                @click="copyUrl(row)"
              >
              </el-link>
            </template>
          </el-table-column>

          <el-table-column label="Uses" width="200">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              {{ row.useCount || 0 }} / {{ row.maxUses || "unlimited" }}
            </template>
          </el-table-column>
          <el-table-column width="100">
            <template #default="{ row }: Slotted<ShortcodeDetail>">
              <el-button size="small" @click="deleteShortcode(row)">
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <ShortcodeEditor
      v-if="selectedRow?.id"
      :key="selectedRow.id"
      :drawer-open="drawerOpen"
      :shortcode-detail="selectedRow"
      :workflow-definition="workflowDefinition"
      :form-definitions-with-multiple-instances="
        multipleInstanceFormDefinitions
      "
      :model-value="drawerOpen"
      @close-drawer="closeDrawer"
      @updated-shortcode="updated"
    />
  </div>
</template>

<style lang="less">
#pane-shortcode-list {
  overflow: auto;
}
.shortcodeListContainer {
  position: relative;
  padding: 16px;

  .code {
    font-family: monospace;
    padding: 2px 4px;
  }
  code {
    font-family: monospace;
    padding-left: 1em;
  }

  .top-right {
    position: absolute;
    top: 16px;
    right: 16px;
  }

  .shortcode-section {
    margin-bottom: 32px;
    box-shadow: 2px 2px 3px 2px rgba(0, 0, 0, 0.4);
    padding: 0.5em 1em 1em;

    h2 {
      margin-bottom: 8px;
      font-size: 18px;
      font-weight: 600;
    }

    .section-description {
      margin-bottom: 16px;
      color: #606266;
      font-size: 14px;
    }
  }
}
</style>
